import getSuggestedAddresses from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import {
  abortThunkOnSignal,
  acceptPlaceCandidate,
  fetchAddressCandidates,
  resolveSuggestions,
  type SuggestEntry,
  type ThunkDispatch,
} from '../../GlobalMapSearch.poi';
import type { SearchResultItem } from '../../GlobalMapSearch.types';
import {
  normalizePlaceCoords,
  PlaceResultDedupe,
} from '../../GlobalMapSearch.utils';

interface RunGeocodeSearchArgs {
  text: string;
  lat?: number;
  lng?: number;
  serviceUrl?: string;
  token?: string | null;
  signal: AbortSignal;
  collected: SearchResultItem[];
  flush: () => void;
  thunkDispatch: ThunkDispatch;
}

/**
 * Generic location lookup. Runs both:
 * - `/findAddressCandidates` (direct free-text geocode), and
 * - `/suggest` + magicKey resolve
 *
 * If map-center coordinates are available we pass them as a location bias;
 * otherwise we still run a global geocode pass so name/address queries are
 * never limited to local GIS-only results.
 */
export async function runGeocodeSearch({
  text,
  lat,
  lng,
  serviceUrl,
  token,
  signal,
  collected,
  flush,
  thunkDispatch,
}: RunGeocodeSearchArgs): Promise<void> {
  let cleanupSuggestAbortListener: (() => void) | undefined;
  try {
    if (signal.aborted) return;

    const suggestRequest = thunkDispatch(
      getSuggestedAddresses?.initiate({
        searchTerm: text.toLowerCase(),
        latitude: lat,
        longitude: lng,
        serviceUrl,
        token,
      }),
    );
    cleanupSuggestAbortListener = abortThunkOnSignal(suggestRequest, signal);

    const [suggestResult, addressCandidates] = await Promise.all([
      suggestRequest,
      fetchAddressCandidates({
        text,
        lng,
        lat,
        serviceUrl,
        token,
        signal,
      }),
    ]);

    if (signal.aborted) return;

    const seen = new PlaceResultDedupe();
    const globalCount = { count: 0 };

    for (const raw of addressCandidates) {
      if (!raw.address || !raw.location) continue;
      const candidate = normalizePlaceCoords(raw);
      if (!acceptPlaceCandidate(candidate, seen, globalCount)) continue;
      collected.push({ type: 'place', data: candidate });
      flush();
    }

    const suggestions = (suggestResult?.data as SuggestEntry[]) || [];

    const resolved = await resolveSuggestions({
      suggestions,
      seen,
      globalCount,
      dispatch: thunkDispatch,
      signal,
    });
    for (const r of resolved) {
      collected.push({ type: 'place', data: r });
      flush();
    }
  } catch {
    // Geocode search failed — leave already-collected results in place.
  } finally {
    cleanupSuggestAbortListener?.();
  }
}
